import { z } from 'zod'
import { LIMITS } from './constants.js'
import { CONTEXT_LIMITS, RELATIONSHIP_TYPES } from './context.js'
import { newId, nowIso } from './ids.js'
import type { BoardAccess } from './access.js'
import { colourSchema, newNodeCore, nodeCoreFields, relationshipProblem, titleSchema, type BoardSnapshot, type Card } from './schemas.js'
import type { CommandResult } from './commands.js'
import {
  CellOrder,
  cardFromNode,
  cardRelationships,
  linksToAdd,
  nodeCardFields,
  PlacementResolver,
  placeCard,
  updateMatchedCard,
  type UpdateSummary,
} from './mindmap-storymap.js'
import type { NormalizedBoard } from './normalized.js'
import type { Operation } from './operations.js'

/**
 * Workflows: a third kind of board, stored as one graph of nodes and connections. Like a
 * mindmap it is small, always loaded and saved whole, so it is kept as one jsonb value.
 */

/** How a node is drawn and connected — presentation. What it means is its `type`. */
export const WORKFLOW_NODE_SHAPES = ['start', 'step', 'decision', 'end'] as const
export type WorkflowNodeShape = (typeof WORKFLOW_NODE_SHAPES)[number]

export const WORKFLOW_NODE_SHAPE_LABELS: Record<WorkflowNodeShape, string> = {
  start: 'Start',
  step: 'Step',
  decision: 'Decision',
  end: 'End',
}

export const WORKFLOW_LIMITS = {
  title: LIMITS.cardTitle,
  description: LIMITS.description,
  edgeLabel: 80,
  nodes: 1_000,
  edges: 2_000,
} as const

/** A node: the fields every item shares, plus its shape and how it is drawn. */
export const workflowNodeSchema = z.object({
  ...nodeCoreFields,
  shape: z.enum(WORKFLOW_NODE_SHAPES),
  colour: colourSchema.default('default'),
  /** Canvas position, in world units. */
  x: z.number().finite(),
  y: z.number().finite(),
})

export type WorkflowNode = z.infer<typeof workflowNodeSchema>

/** A connection from one node to the next; a decision's outgoing connections carry its options. */
export const workflowEdgeSchema = z.object({
  id: z.uuid(),
  source: z.uuid(),
  target: z.uuid(),
  label: z
    .string()
    .transform((s) => s.trim())
    .pipe(z.string().max(WORKFLOW_LIMITS.edgeLabel, `Maximum ${WORKFLOW_LIMITS.edgeLabel} characters`)),
  /** What the connection means, from the same vocabulary as relationships; null is a plain flow. */
  type: z.enum(RELATIONSHIP_TYPES).nullable().default(null),
})

export type WorkflowEdge = z.infer<typeof workflowEdgeSchema>

/** The graph itself: what the board row's `workflow` column holds. */
export const workflowGraphSchema = z
  .object({
    nodes: z.array(workflowNodeSchema).max(WORKFLOW_LIMITS.nodes, `A workflow is limited to ${WORKFLOW_LIMITS.nodes} nodes`),
    edges: z.array(workflowEdgeSchema).max(WORKFLOW_LIMITS.edges, `A workflow is limited to ${WORKFLOW_LIMITS.edges} connections`),
  })
  .superRefine((graph, ctx) => {
    const ids = new Set(graph.nodes.map((node) => node.id))
    if (ids.size !== graph.nodes.length) ctx.addIssue({ code: 'custom', message: 'Node ids must be unique', path: ['nodes'] })
    for (const [index, edge] of graph.edges.entries()) {
      if (!ids.has(edge.source) || !ids.has(edge.target)) {
        ctx.addIssue({ code: 'custom', message: 'A connection must join two nodes of this workflow', path: ['edges', index] })
      } else if (edge.source === edge.target) {
        ctx.addIssue({ code: 'custom', message: 'A node cannot connect to itself', path: ['edges', index] })
      }
    }
    const problem = relationshipProblem(graph.nodes)
    if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['nodes'] })
  })

export type WorkflowGraph = z.infer<typeof workflowGraphSchema>

/** Wire shape for a whole workflow: `GET /api/workflows/:id`. */
export const workflowSchema = z.object({
  id: z.uuid(),
  name: titleSchema,
  revision: z.number().int().min(0),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  graph: workflowGraphSchema,
})

export type Workflow = z.infer<typeof workflowSchema>

/** A workflow as the API returns it: with how the asking user reaches it. */
export type WorkflowView = Workflow & { access: BoardAccess }

/** `POST /api/workflows`: a client-generated id, so a lost response can be retried. */
export const workflowCreateSchema = z.object({
  id: z.uuid(),
  name: titleSchema,
  /** The project to create it in; the user's default project when left out. */
  projectId: z.uuid().optional(),
})

/** `PUT /api/workflows/:id`: the whole graph, against the revision the client last saw. */
export const workflowSaveSchema = z.object({
  expectedRevision: z.number().int().min(0),
  name: titleSchema.optional(),
  graph: workflowGraphSchema,
})

export type WorkflowSave = z.infer<typeof workflowSaveSchema>

export function newWorkflowNode(shape: WorkflowNodeShape, x = 0, y = 0, title: string = WORKFLOW_NODE_SHAPE_LABELS[shape]): WorkflowNode {
  return { ...newNodeCore(title), shape, colour: 'default', x, y }
}

/** A new workflow: a Start connected to an End. */
export function createWorkflow(input: { id?: string; name: string; now?: string }): Workflow {
  const now = input.now ?? nowIso()
  const start = newWorkflowNode('start', 0, 0)
  const end = newWorkflowNode('end', 300, 0)
  return {
    id: input.id ?? newId(),
    name: input.name.trim(),
    revision: 0,
    createdAt: now,
    updatedAt: now,
    graph: { nodes: [start, end], edges: [{ id: newId(), source: start.id, target: end.id, label: '', type: null }] },
  }
}

// --- turning a workflow into a story map ---------------------------------------------

/**
 * Every node becomes a card, placed by its group, column, swimlane and row titles. A node
 * without a column goes to "Unplaced"; one without a swimlane to "Workflow", in its first
 * row. Each card records its node (`sourceNodeId`) and the placement it was put by; the
 * board records the workflow, so it can be brought up to date later.
 */

export const UNPLACED_COLUMN_TITLE = 'Unplaced'
export const WORKFLOW_SWIMLANE_TITLE = 'Workflow'

/** The story maps generated from a workflow that the asking user can reach. */
export interface WorkflowLinks {
  boards: { id: string; name: string; access: BoardAccess }[]
}

/** Left to right, then top to bottom: the order the editor's tidy layout reads in. */
const flowOrder = (nodes: readonly WorkflowNode[]) => [...nodes].sort((a, b) => a.x - b.x || a.y - b.y)

const workflowDefaults = (resolver: PlacementResolver) => ({
  columnId: () => resolver.column(UNPLACED_COLUMN_TITLE, ''),
  rowId: () => resolver.firstRow(resolver.swimlane(WORKFLOW_SWIMLANE_TITLE)),
})

/** Each node with its typed outgoing connections added to its relationships, so they carry to cards like its own. */
function withConnections(graph: WorkflowGraph): WorkflowNode[] {
  return graph.nodes.map((node) => ({
    ...node,
    relationships: [
      ...node.relationships,
      ...graph.edges.flatMap((edge) =>
        edge.source === node.id && edge.type
          ? [{ id: edge.id, type: edge.type, targetId: edge.target, description: edge.label, protocol: '', contract: '', mode: '' as const }]
          : [],
      ),
    ],
  }))
}

/** A complete new board for a workflow, ready for `POST /api/boards/import`. */
export function workflowToSnapshot(input: { workflowId: string; graph: WorkflowGraph; name: string; boardId?: string; now?: string }): BoardSnapshot {
  const now = input.now ?? nowIso()
  const boardId = input.boardId ?? newId()
  const resolver = new PlacementResolver({ groups: [], columns: [], swimlanes: [], rows: [] })
  const defaults = workflowDefaults(resolver)
  const nodes = flowOrder(withConnections(input.graph))
  const cards: Card[] = []
  const position = new Map<string, number>()
  for (const node of nodes) {
    const { columnId, rowId } = placeCard(resolver, node.placement, defaults)
    const card = cardFromNode(boardId, node, { rowId: rowId!, columnId: columnId! }, node.colour, now)
    const cell = `${card.rowId}|${card.columnId}`
    card.position = position.get(cell) ?? 0
    position.set(cell, card.position + 1)
    cards.push(card)
  }
  // The nodes' relationships, now every card has an id.
  const cardIdByNode = new Map(cards.map((card) => [card.sourceNodeId!, card.id]))
  for (const [index, node] of nodes.entries()) cards[index]!.relationships = cardRelationships(node, cardIdByNode).slice(0, CONTEXT_LIMITS.relationshipCount)
  // A board always has somewhere to put a card.
  if (cards.length === 0) {
    defaults.columnId()
    defaults.rowId()
  }
  const { groups, columns, swimlanes, rows } = resolver.made
  return {
    board: {
      id: boardId,
      name: input.name.trim().slice(0, LIMITS.title),
      revision: 0,
      createdAt: now,
      updatedAt: now,
      source: { workflowId: input.workflowId, generatedAt: now },
    },
    columnGroups: groups.map((group) => ({ ...group, boardId })),
    columns: columns.map((column, index) => ({ ...column, boardId, position: index })),
    swimlanes: swimlanes.map((swimlane, index) => ({ ...swimlane, boardId, position: index })),
    rows: rows.map((row) => ({ ...row, boardId, position: rows.filter((other) => other.swimlaneId === row.swimlaneId).indexOf(row) })),
    cards,
  }
}

/**
 * Brings a board generated from a workflow up to date with it, as one undoable command.
 * Matching is by `sourceNodeId`: a matched card takes its node's title, description and
 * tags, and moves only when the node's placement changed. New nodes are added; cards
 * whose node is gone, and everything added by hand, are left alone.
 */
export function planWorkflowUpdate(state: NormalizedBoard, graph: WorkflowGraph): { result: CommandResult | null; summary: UpdateSummary } {
  const boardId = state.board.id
  const now = nowIso()
  const summary: UpdateSummary = { updated: 0, added: 0, sourceRemoved: 0 }
  const resolver = PlacementResolver.fromBoard(state)
  const defaults = workflowDefaults(resolver)
  const order = new CellOrder(state)
  const bySource = new Map<string, Card>()
  for (const card of Object.values(state.cards)) if (card.sourceNodeId && !bySource.has(card.sourceNodeId)) bySource.set(card.sourceNodeId, card)

  const forward: Operation[] = []
  const inverse: Operation[] = []
  const created: Card[] = []
  const nodes = flowOrder(withConnections(graph))
  const cardIdByNode = new Map<string, string>()
  for (const node of nodes) {
    const place = () => placeCard(resolver, node.placement, defaults)
    const existing = bySource.get(node.id)
    if (existing) {
      cardIdByNode.set(node.id, existing.id)
      const changes = updateMatchedCard(order, existing, { ...nodeCardFields(node), colour: node.colour }, node.placement, place)
      if (changes.forward.length > 0) {
        forward.push(...changes.forward)
        inverse.unshift(...changes.inverse)
        summary.updated += 1
      }
      continue
    }
    const { rowId, columnId } = place()
    const card = cardFromNode(boardId, node, { rowId: rowId!, columnId: columnId! }, node.colour, now)
    cardIdByNode.set(node.id, card.id)
    created.push(card)
    summary.added += 1
  }
  for (const card of created) {
    const node = nodes.find((candidate) => candidate.id === card.sourceNodeId)!
    card.relationships = cardRelationships(node, cardIdByNode).slice(0, CONTEXT_LIMITS.relationshipCount)
    const { position: _position, updatedAt: _updatedAt, ...input } = card
    forward.push({ type: 'card.create', card: input, beforeId: null })
    inverse.unshift({ type: 'card.delete', id: card.id })
  }
  // A matched card gains the relationships and references it lacks, after the new cards it may point at exist.
  for (const node of nodes) {
    const existing = bySource.get(node.id)
    const links = existing ? linksToAdd(existing, node, cardIdByNode) : null
    if (!existing || !links) continue
    forward.push({ type: 'card.update', id: existing.id, patch: links.patch })
    inverse.unshift({ type: 'card.update', id: existing.id, patch: links.before })
  }

  const live = new Set(graph.nodes.map((node) => node.id))
  for (const card of Object.values(state.cards)) if (card.sourceNodeId && !live.has(card.sourceNodeId)) summary.sourceRemoved += 1

  const placed = resolver.operations(boardId)
  if (placed.forward.length + forward.length === 0) return { result: null, summary }
  return { result: { label: 'Update from workflow', forward: [...placed.forward, ...forward], inverse: [...inverse, ...placed.inverse] }, summary }
}
